/**
 * Send a document to the printer.
 *
 * The document is written into an offscreen iframe and printed from there, which
 * is how the legacy version did it (doctor_dashboard.js:20660) and is still the
 * right approach: `window.open` is blocked by popup blockers when it is not
 * called straight from a click, and printing the app's own page — which is what
 * the rebuilt button did — prints the dark UI instead of the letterhead.
 *
 * The browser's own print dialog is also the download path: "Save as PDF" is a
 * destination in it, so preview, print and download are one button rather than
 * a PDF library in the bundle.
 */

import { buildPrescriptionHtml } from '../domain/prescriptionPrint';
import { buildMedicalRecordHtml } from '../domain/medicalRecordPrint';
import { poweredByLine } from '../domain/branding';
import { currentBranding } from './branding';

/** Give the fonts and any data: images a moment before the dialog opens. */
const RENDER_DELAY_MS = 350;

/** How long to leave the frame in the DOM after printing. */
const CLEANUP_DELAY_MS = 1000;

/** The platform's line under every sheet: the name the Super Admin set. */
const platformLine = () => ({ poweredBy: poweredByLine(currentBranding()) });

/**
 * @param {object} args — passed straight to `buildPrescriptionHtml`
 * @returns {Promise<void>} resolves once the print dialog has been asked for
 */
export function printPrescription(args) {
  return printDocument(buildPrescriptionHtml({ ...platformLine(), ...args }));
}

/**
 * The patient's whole medical record, as a multi-page A4 document.
 *
 * @param {object} args — passed straight to `buildMedicalRecordHtml`
 * @returns {Promise<void>}
 */
export function printMedicalRecord(args) {
  return printDocument(buildMedicalRecordHtml({ ...platformLine(), ...args }));
}

/**
 * المرحلة 1.4 — the visit's papers (the report, a certificate, a referral):
 * built by domain/visitReportPrint.js with the platform's line under them,
 * printed through the same frame.
 *
 * @param {(args: object) => string} build one of the visitReportPrint builders
 * @param {object} args passed to it
 */
export function printVisitPaper(build, args) {
  return printDocument(build({ ...platformLine(), ...args }));
}

/**
 * Print a finished HTML document.
 *
 * The record can carry a dozen embedded result images, so it gets a longer
 * settle before the dialog opens — printing while a data: image is still
 * decoding produces a blank frame where the picture should be.
 */
function printDocument(html) {
  const heavy = html.length > 400_000;
  const delay = heavy ? RENDER_DELAY_MS * 4 : RENDER_DELAY_MS;

  return new Promise((resolve, reject) => {
    const frame = document.createElement('iframe');
    // Offscreen rather than display:none — a hidden frame does not lay out in
    // some browsers, and an unlaid-out document prints blank.
    frame.setAttribute('aria-hidden', 'true');
    frame.style.position = 'fixed';
    frame.style.right = '-10000px';
    frame.style.bottom = '0';
    frame.style.width = '794px';
    frame.style.height = '1123px';
    frame.style.border = '0';

    let settled = false;
    let cleanupTimer = null;

    const cleanup = () => {
      if (cleanupTimer) clearTimeout(cleanupTimer);
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    frame.onload = () => {
      // `onload` fires for the initial about:blank too; only act once the
      // document has been written into.
      const win = frame.contentWindow;
      if (!win || !win.document.body || win.document.body.childElementCount === 0) return;

      setTimeout(() => {
        try {
          win.focus();
          win.print();
          if (!settled) {
            settled = true;
            resolve();
          }
        } catch (error) {
          fail(error);
          return;
        }
        // The frame has to outlive print() — some browsers render the dialog
        // asynchronously and print a blank sheet if the document is gone.
        cleanupTimer = setTimeout(cleanup, CLEANUP_DELAY_MS);
      }, delay);
    };

    document.body.appendChild(frame);

    try {
      const doc = frame.contentWindow?.document;
      if (!doc) throw new Error('تعذر تجهيز صفحة الطباعة');
      doc.open();
      doc.write(html);
      doc.close();
    } catch (error) {
      fail(error);
    }
  });
}

export default printPrescription;
